// Copyright (c) Meta Platforms, Inc. and affiliates.

/**
 * @file CheckboxIndicator.tsx
 * @input Indicator state props
 * @output Exports CheckboxIndicator — the default checkbox selection visual
 * @position Decorative checkbox visual shared by CheckboxInput, CheckboxList,
 *           and decorative menu markers
 */

import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { borderVars, colorVars, durationVars, easeVars, radiusVars } from "../theme/tokens.stylex.js";
import { isRenderable, mergeProps, themeProps } from "../utils/index.js";
import "./indicator.markers.stylex.js";
import { indicatorScope } from "./indicator.markers.stylex.js";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const styles = {
  box: {
    kB7OPa: "x9f619",
    k1xSpc: "x78zum5",
    kGNEyG: "x6s0dn4",
    kjj79g: "xl56j7k",
    kmuXW: "x2lah0s",
    kMzoRj: "x1litavf",
    ksu8eU: "x1y0btm7",
    kaIpWk: "xx3sua9",
    k1ekBW: "xts7igz",
    kIyJzY: "xuedmi6 x12w9bfk",
    kAMwcw: "xlr8y92",
    $$css: true
  },
  unchecked: {
    kMwMTN: "xqwr325",
    kVAM5u: "xvy26l8 xilc7fp",
    kWkggS: "x10xzikg xeultfy",
    $$css: true
  },
  checked: {
    kMwMTN: "x17wrial",
    kVAM5u: "xad5do x1ozl1p1",
    kWkggS: "x1ewilqj x17srpfb",
    $$css: true
  },
  disabled: {
    kSiTet: "xbyyjgo",
    kVAM5u: "x14i3s5s x61roa7",
    $$css: true
  },
  disabledUnchecked: {
    kWkggS: "xwmxj5m xqxnq6d",
    $$css: true
  },
  checkmark: {
    k1xSpc: "x1s85apg",
    kMwMTN: "x17wrial xs5hli",
    $$css: true
  },
  checkmarkVisible: {
    k1xSpc: "x1lliihq",
    $$css: true
  },
  indeterminateMark: {
    k1xSpc: "x1s85apg",
    kWkggS: "x1azo05 xwvh9j7",
    kaIpWk: "xjspbzw",
    $$css: true
  },
  indeterminateMarkVisible: {
    k1xSpc: "x1lliihq",
    $$css: true
  }
};
const boxSizeStyles = {
  sm: {
    kzqmXN: "xw4jnvo",
    kZKoxP: "x1qx5ct2",
    $$css: true
  },
  md: {
    kzqmXN: "xvy4d1p",
    kZKoxP: "xxk0z11",
    $$css: true
  }
};
const checkmarkSizeStyles = {
  sm: {
    kzqmXN: "xsmyaan",
    kZKoxP: "x1kpxq89",
    $$css: true
  },
  md: {
    kzqmXN: "x6jxa94",
    kZKoxP: "x1v9usgg",
    $$css: true
  }
};
const indeterminateSizeStyles = {
  sm: {
    kzqmXN: "x1fsd2vl",
    kZKoxP: "x36qwtl",
    $$css: true
  },
  md: {
    kzqmXN: "xsmyaan",
    kZKoxP: "x36qwtl",
    $$css: true
  }
};

/**
 * The default checkbox visual: a square box with a checkmark or an
 * indeterminate bar.
 *
 * Decorative and non-interactive — it renders `aria-hidden` and owns no input,
 * role, or focus behavior. The focus ring lives on the owner's control wrapper
 * (see CheckboxInput), so a theme that replaces this component keeps a visible
 * focus indicator for free. Themes replace it wholesale through
 * `defineTheme({indicators: {checkbox: MyCheckbox}})`, or restyle it through
 * the `checkbox` theme target like any other component.
 *
 * @example
 * ```
 * <CheckboxIndicator state="indeterminate" size="sm" />
 * ```
 */
export function CheckboxIndicator({
  state,
  size = 'md',
  isDisabled = false,
  children,
  ref,
  className,
  style,
  xstyle,
  ...rest
}) {
  const isChecked = state === 'checked';
  const isIndeterminate = state === 'indeterminate';
  const isCheckedOrIndeterminate = isChecked || isIndeterminate;
  return /*#__PURE__*/_jsx("span", {
    ...rest,
    ref: ref,
    "aria-hidden": "true",
    ...mergeProps(themeProps('checkbox-indicator', {
      size,
      checked: isChecked ? 'checked' : isIndeterminate ? 'indeterminate' : null,
      disabled: isDisabled ? 'disabled' : null
    },
    // `checkbox` was the target before indicators existed; themes
    // styling it keep working until the next major.
    {
      legacyNames: ['checkbox']
    }), stylex.props(styles.box, boxSizeStyles[size], isCheckedOrIndeterminate ? styles.checked : styles.unchecked, isDisabled && styles.disabled, isDisabled && !isCheckedOrIndeterminate && styles.disabledUnchecked, xstyle), className, style),
    children: isRenderable(children) ? children : /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx("svg", {
        viewBox: "0 0 10 10",
        ...stylex.props(styles.checkmark, checkmarkSizeStyles[size], isChecked && styles.checkmarkVisible),
        children: /*#__PURE__*/_jsx("path", {
          d: "M8.5 2.5L4 7.5L1.5 5",
          stroke: "currentColor",
          strokeWidth: "1.5",
          fill: "none",
          strokeLinecap: "round",
          strokeLinejoin: "round"
        })
      }), /*#__PURE__*/_jsx("span", {
        ...stylex.props(styles.indeterminateMark, indeterminateSizeStyles[size], isIndeterminate && styles.indeterminateMarkVisible)
      })]
    })
  });
}
CheckboxIndicator.displayName = 'CheckboxIndicator';